// ---------------------------------------------------------------------------
// Tenant Admin module — SSO Connections list page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Add SSO connection form
// ---------------------------------------------------------------------------

function AddSsoConnectionForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation('admin');
  const [displayName, setDisplayName] = useState('');
  const [providerType, setProviderType] = useState<'oidc' | 'saml'>('oidc');
  const [issuerUrl, setIssuerUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = `sso_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await fetch(`${apiBase}/v1/admin/tenants/${tenantId}/sso-connections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({
          displayName,
          providerType,
          config: { issuerUrl: issuerUrl || undefined },
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      setDisplayName('');
      setProviderType('oidc');
      setIssuerUrl('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('sso.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('sso.form.title')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label htmlFor="sso-name" className="block text-sm font-medium text-ink mb-1">
            {t('sso.form.providerName')}
          </label>
          <input
            id="sso-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('sso.form.providerNamePlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="sso-type" className="block text-sm font-medium text-ink mb-1">
            {t('sso.form.type')}
          </label>
          <select
            id="sso-type"
            value={providerType}
            onChange={(e) => setProviderType(e.target.value as 'oidc' | 'saml')}
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-raised"
          >
            <option value="oidc">{t('sso.providerTypes.oidc')}</option>
            <option value="saml">{t('sso.providerTypes.saml')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="sso-issuer" className="block text-sm font-medium text-ink mb-1">
            {t('sso.form.issuerUrl')}
          </label>
          <input
            id="sso-issuer"
            type="url"
            value={issuerUrl}
            onChange={(e) => setIssuerUrl(e.target.value)}
            placeholder={t('sso.form.issuerUrlPlaceholder')}
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('sso.actions.creating') : t('sso.actions.create')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SsoPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const providerTypeLabels = t('sso.providerTypes', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.SsoConnectionSummary>[]>(
    () => [
      {
        key: 'displayName',
        header: t('sso.columns.name'),
        render: (s) => (
          <span className="text-sm font-medium text-ink">
            {s.displayName}
          </span>
        ),
      },
      {
        key: 'providerType',
        header: t('sso.columns.providerType'),
        render: (s) => (
          <span className="text-sm text-ink-muted">
            {providerTypeLabels[s.providerType] ?? s.providerType}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('sso.columns.status'),
        render: (s) => <StatusBadge value={s.status} />,
      },
      {
        key: 'createdAt',
        header: t('sso.columns.created'),
        render: (s) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(s.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t, providerTypeLabels],
  );
  const { tenantId } = useProjectContext();
  const [connections, setConnections] = useState<admin.SsoConnectionSummary[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadConnections = useCallback(() => {
    if (!tenantId) {
      setConnections([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    api.admin
      .listSsoConnections(tenantId)
      .then((res) => setConnections(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('sso.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [tenantId, t]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  if (!tenantId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">
          {t('sso.headings.list')}
        </h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('sso.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadConnections} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('sso.headings.list')}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
        >
          {showCreate ? t('sso.actions.cancel') : t('sso.actions.add')}
        </button>
      </div>

      {showCreate && (
        <AddSsoConnectionForm
          tenantId={tenantId}
          onCreated={() => {
            setShowCreate(false);
            loadConnections();
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={connections}
        rowKey={(s) => s.id}
        emptyMessage={t('sso.empty')}
        ariaLabel={t('sso.ariaLabel')}
      />
    </div>
  );
}
